"use client";
import {
  Box,
  Flex,
  Heading,
  Menu,
  MenuList,
  Portal,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { PressItemCard } from "./press-item-card";
import {
  PostsFilterBar,
  PressItem,
  pressPostsControllerAtom,
  usePostsFilter,
} from "@ui/post/filter-bar";
import { useAtomValue } from "jotai";
import { FilterMenuButton } from "@ui/components/menu/menu-button";
import FilterBarMenuItem from "@ui/components/menu/menu-item";
import { BiSort } from "react-icons/bi";
import { FaChevronDown } from "react-icons/fa";
import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import { capitalCase } from "change-case";
import { ThemeColor } from "lib/gcms/theme-color";
import { LoadingArea } from "@ui/loading";

export interface PressItemSectionProps {
  backgroundColor: ThemeColor;
  textColor: ThemeColor;
  pressItems: PressItem[];
}

export const PressItemSection = ({ pressItems }: PressItemSectionProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Checks for any preselected filter from the search params (i.e. white paper)
  const searchParams = useSearchParams();
  const preselectedFilter = searchParams?.get("selectedFilter");

  const {
    selectedPosts,
    onFilterChange,
    onSortChange,
    filterOptions,
    sortOptions,
  } = usePostsFilter(
    { pressItems },
    "news-room",
    preselectedFilter ? capitalCase(preselectedFilter as string) : undefined,
  );
  const postController = useAtomValue(pressPostsControllerAtom);
  const indexOfLastPost = currentPage * 9;
  const indexOfFirstPost = indexOfLastPost - 9;
  const currentPosts = selectedPosts.slice(indexOfFirstPost, indexOfLastPost);
  const pageNumbers = Array.from(
    { length: Math.ceil(selectedPosts.length / 9) },
    (_, idx) => idx + 1,
  );

  return (
    <Box
      maxW={{ base: "full", lg: "7xl" }}
      mx="auto"
      pb="24"
      position="relative"
    >
      <Suspense fallback={<LoadingArea h="100vh" w="full" />}>
        {/* Filtering Features */}
        <PostsFilterBar
          {...{
            selectedPosts,
            onFilterChange,
            onSortChange,
            filterOptions,
            sortOptions,
            buttonColors: [
              "#EF4444",
              "#21C55D",
              "#3B81F6",
              "#A854F7",
              "#F87315",
            ],
            from: "news-room",
            setCurrentPage: setCurrentPage,
          }}
        />
      </Suspense>

      <Flex justify={"end"} mt={10} mb={3}>
        <Menu isLazy>
          <FilterMenuButton
            leftIcon={<BiSort />}
            rightIcon={<FaChevronDown />}
            value={postController.sort}
            from="news-room"
          >
            {postController.sort}
          </FilterMenuButton>

          <Portal>
            <MenuList zIndex={10}>
              {sortOptions.map((option, i) => (
                <FilterBarMenuItem
                  key={`${option}-${i}`}
                  value={option}
                  onClick={onSortChange}
                >
                  {option}
                </FilterBarMenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
      {/* Trending Posts Section */}
      {postController.filter === "All" &&
      postController.sort === "Sort" &&
      currentPage === 1 ? (
        <Box
          bgColor="#0A66C2"
          borderRadius={20}
          w="100%"
          pb={{
            base: 10,
            lg: 0,
          }}
          h={{
            base: "100%",
            md: "930px",
            lg: "350px",
          }}
          mb={{
            base: 16,
            lg: 48,
          }}
          zIndex="0"
        >
          <Heading
            mt={3}
            textAlign="center"
            zIndex="1"
            position="relative"
            top={9}
            color="white"
          >
            Top Stories
          </Heading>
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacingX={{ base: "14", md: "20", lg: "14", xl: "24" }}
            spacingY={{
              base: "10",
              md: "16",
            }}
            mx="auto"
            mt={20}
            px={{ base: "6", md: "12" }}
          >
            {currentPosts.slice(0, 3).map((item, idx) => (
              <PressItemCard {...item} key={idx} />
            ))}
          </SimpleGrid>
        </Box>
      ) : (
        <></>
      )}
      {/* Rest of the Posts */}
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3 }}
        spacingX={{ base: "14", md: "20", lg: "14", xl: "24" }}
        spacingY={{
          base: "10",
          md: "16",
        }}
        mx="auto"
        px={{ base: "6", md: "12" }}
      >
        {(postController.filter === "All" &&
        postController.sort === "Sort" &&
        currentPage === 1
          ? currentPosts.slice(3)
          : currentPosts
        ).map((item, idx: number) => (
          <PressItemCard {...item} key={idx} />
        ))}
      </SimpleGrid>
      <Flex
        mt={10}
        justify="end"
        align="center"
        mr={{
          base: 7,
          md: 12,
        }}
        color="black"
        fontSize={{
          base: 13,
          lg: 14,
        }}
      >
        <Text
          pr={{
            base: 8,
            md: 10,
          }}
        >
          Page
        </Text>
        {pageNumbers.map((num, idx) => {
          const isNotLastPage = num !== pageNumbers[pageNumbers.length - 1];
          return (
            <Text
              key={`page-btn-${idx}`}
              onClick={() => setCurrentPage(num)}
              variant="unstyled"
              fontWeight={currentPage === num ? "bold" : "normal"}
              fontSize={{
                base: 13,
                lg: 14,
              }}
              cursor="pointer"
              pr={{
                base: isNotLastPage ? 3 : 0,
                md: isNotLastPage ? 5 : 0,
              }}
            >
              {num}
            </Text>
          );
        })}
      </Flex>
    </Box>
  );
};
